import { useMutation, useQuery, type ApolloError } from '@apollo/client'
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Popover,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { type FC, useState } from 'react'
import {
    compactListboxProps,
    minimalInputSx,
} from 'recruiter/components/CandidateTableToolbar/minimalFilterStyles'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { AddCandidatesToRoster } from 'recruiter/graphql/rosters.gql'
import useToast from 'shared/components/Toast/useToast'
import {
    formatGraphQLError,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'

interface RosterSearchAddToRosterProps {
    selectedCandidateIds: string[]
    rosterId: string
    onClose: () => void
    onSuccess: () => void
    isOpen: boolean
    anchorEl: HTMLElement | null
}

export const RosterSearchAddToRoster: FC<RosterSearchAddToRosterProps> = ({
    selectedCandidateIds,
    rosterId,
    onClose,
    onSuccess,
    isOpen,
    anchorEl,
}) => {
    const toast = useToast()
    const [isLoading, setIsLoading] = useState(false)

    const [addCandidatesToRoster] = useMutation(AddCandidatesToRoster, {
        onCompleted: (data) => {
            toast({
                title: 'Success',
                message:
                    data?.addCandidatesToRoster.message ||
                    'Candidate(s) added to roster',
                severity: 'success',
            })
            handleCancel()
            setIsLoading(false)
            onSuccess()
        },
        onError: (error: ApolloError) => {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error adding candidate(s) to roster'
                ),
                severity: 'error',
            })
            setIsLoading(false)
        },
    })

    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)

    const [selectedProcessStatus, setSelectedProcessStatus] =
        useState<string>('')
    const [overwriteExistingStatus, setOverwriteExistingStatus] =
        useState<boolean>(false)

    const processStatusValue =
        processStatusOptions?.processStatuses.find(
            (status) => status.id === selectedProcessStatus
        ) || null

    const handleCancel = () => {
        setSelectedProcessStatus('')
        setOverwriteExistingStatus(false)
        onClose()
    }

    const handleSave = async () => {
        if (!selectedProcessStatus) {
            toast({
                title: 'Error',
                message: 'Please select a process status',
                severity: 'error',
            })
            return
        }

        setIsLoading(true)
        await addCandidatesToRoster({
            refetchQueries: [GetCandidatesForRosterSql],
            variables: {
                rosterId,
                candidateIds: selectedCandidateIds,
                processStatusId: selectedProcessStatus,
                overwriteExistingStatus,
            },
        })
    }

    const buttonSx = {
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        fontWeight: 500,
        py: 0.5,
        px: 1.5,
    }

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            slotProps={{
                paper: {
                    sx: {
                        p: 2,
                        minWidth: 280,
                    },
                },
            }}
        >
            <Stack spacing={1.5}>
                <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                    Add to This Search
                </Typography>

                <Autocomplete
                    size="small"
                    options={sortProcessStatusesBySummaryGroup(
                        processStatusOptions?.processStatuses ?? []
                    )}
                    value={processStatusValue}
                    getOptionLabel={(option) => option.statusLabel}
                    isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                    }
                    groupBy={(option) =>
                        option.processStatusSummaryGroup?.label || ''
                    }
                    fullWidth
                    renderInput={(renderParams) => (
                        <TextField
                            {...renderParams}
                            placeholder="Select process status..."
                            sx={minimalInputSx}
                        />
                    )}
                    renderGroup={(params) => (
                        <Box key={params.group}>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.6875rem',
                                    px: 1,
                                    pt: 0.5,
                                }}
                            >
                                {params.group}
                            </Typography>
                            {params.children}
                        </Box>
                    )}
                    ListboxProps={compactListboxProps}
                    onChange={(_, value) => {
                        setSelectedProcessStatus(value?.id || '')
                    }}
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={overwriteExistingStatus}
                            onChange={(event) =>
                                setOverwriteExistingStatus(event.target.checked)
                            }
                            sx={{ p: 0.5 }}
                        />
                    }
                    label="Update existing candidates' status"
                    sx={{
                        ml: 0,
                        mr: 0,
                        '& .MuiFormControlLabel-label': {
                            fontSize: '0.6875rem',
                        },
                    }}
                />

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancel}
                        disabled={isLoading}
                        sx={{
                            ...buttonSx,
                            borderColor: 'divider',
                            color: 'text.secondary',
                            '&:hover': {
                                borderColor: 'text.secondary',
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSave}
                        disabled={!selectedProcessStatus || isLoading}
                        sx={buttonSx}
                    >
                        {isLoading
                            ? 'Adding...'
                            : `Add (${selectedCandidateIds.length})`}
                    </Button>
                </Stack>
            </Stack>
        </Popover>
    )
}
